import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Stack, Menu, MenuItem, Checkbox, ListItemText, Divider, Typography, Dialog, DialogTitle, 
  DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, Box
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import SignalCellular0BarIcon from '@mui/icons-material/SignalCellular0Bar';
import SignalCellular1BarIcon from '@mui/icons-material/SignalCellular1Bar';
import SignalCellular2BarIcon from '@mui/icons-material/SignalCellular2Bar';
import SignalCellular3BarIcon from '@mui/icons-material/SignalCellular3Bar';
import SignalCellular4BarIcon from '@mui/icons-material/SignalCellular4Bar';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import Battery50Icon from '@mui/icons-material/Battery50';
import Battery80Icon from '@mui/icons-material/Battery80';
import Battery20Icon from '@mui/icons-material/Battery20';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import imageCompression from 'browser-image-compression';



import './AdminDashboard.css';

import API_BASE_URL from '../../config';
import { DevicesSharp } from '@mui/icons-material';

const AdminDashboard = () => {

  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [openAddNoteDialog, setOpenAddNoteDialog] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [serviceNote, setServiceNote] = useState({
    severity: 'green',
    serviceType: 'inspection',
    performedBy: '',
    description: '',
    serviceDescription: '',
    internalNotes: '',
    images: []
  });

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/device-list`, {
        headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
    }

      const deviceStatus = await response.json();

      setDevices(deviceStatus || []);
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };

  const [searchTerms, setSearchTerms] = useState({
    customer: '',
    name: '',
    type: '',
    location: '',
    direction: '',
    connected: '',
    mode: '',
    signal: '',
    battery: '',
    batterytemp: '',
    lastUpdate: ''
  });

  const [filters, setFilters] = useState({
    customer: [],
    type: [],
    location: [],
    direction: []
  });
  
  const [sorting, setSorting] = useState({
    column: null,
    direction: 'asc'
  });

  const hasActiveFilters = Object.values(searchTerms).some(term => term !== '');
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterColumn, setFilterColumn] = useState(null);

  const handleFilterClick = (event, column) => {
    setAnchorEl(event.currentTarget);
    setFilterColumn(column);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
    setFilterColumn(null);
  };

  const handleFilterChange = (column, value) => {
    setFilters(prev => ({
      ...prev,
      [column]: prev[column].includes(value)
        ? prev[column].filter(item => item !== value)
        : [...prev[column], value]
    }));
  };

  const handleSort = (column) => {
    setSorting(prev => {
      if (prev.column !== column) {
        return { column, direction: 'asc' };
      }
      switch (prev.direction) {
        case 'none':
          return { column, direction: 'asc' };
        case 'asc':
          return { column, direction: 'desc' };
        case 'desc':
        default:
          return { column: null, direction: 'none' };
      }
    });
  };
  
  const handleSearch = (column, value) => {
    setSearchTerms(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const handleClearAll = () => {
    setSearchTerms({
      customer: '',
      name: '',
      type: '',
      location: '',
      direction: '',
      connected: '',
      mode: '',
      signal: '',
      battery: '',
      batterytemp: '',
      lastUpdate: ''
    });
  };

  const getFilteredAndSortedDevices = () => {
    // First, apply filters
    let filteredDevices = devices.filter(device => 
      Object.keys(filters).every(key => 
        filters[key].length === 0 || filters[key].includes(device[key])
      )
    );
  
    // Then, apply search terms
    filteredDevices = filteredDevices.filter(device => 
      Object.keys(searchTerms).every(key => {
        const searchTerm = searchTerms[key].toLowerCase();
        const deviceValue = String(device[key]).toLowerCase();
        return !searchTerm || deviceValue.includes(searchTerm);
      })
    );
  
    // Finally, apply sorting
    if (sorting.column && sorting.direction !== 'none') {
      filteredDevices.sort((a, b) => {
        const aVal = a[sorting.column];
        const bVal = b[sorting.column];
        
        if (sorting.direction === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        }
        return bVal > aVal ? 1 : bVal < aVal ? -1 : 0;
      });
    }
  
    return filteredDevices;
  };

  const getSignalIcon = (value) => {
    const bars = Math.max(0, Math.min(5, Math.floor((value + 109) / 12) + 1));

    // Clamp the dBm value between -113 and -51
    const clampedDBm = Math.max(-113, Math.min(value, -51));

    // Calculate the percentage of signal strength
    const signalPercentage = (clampedDBm + 113) / (62) * 100;

    if (signalPercentage >= 80) return <SignalCellular4BarIcon color="success" />;
    if (signalPercentage >= 60) return <SignalCellular3BarIcon color="success" />;
    if (signalPercentage >= 40) return <SignalCellular2BarIcon color="warning" />;
    if (signalPercentage >= 20) return <SignalCellular1BarIcon color="error" />;
    return <SignalCellular0BarIcon color="error" />;
  };

  const getBatteryIcon = (percentage) => {
    if (percentage === -1) return <BatteryAlertIcon color="error" />;
    if (percentage >= 80) return <BatteryFullIcon color="success" />;
    if (percentage >= 50) return <Battery80Icon color="success" />;
    if (percentage >= 20) return <Battery50Icon color="warning" />;
    return <Battery20Icon color="error" />;
  };

  const getTimeSinceLastUpdate = (lastUpdate) => {
    if (!lastUpdate) return { text: 'Unknown', color: '#ffffff' };
    
    try {
      const lastUpdateTime = new Date(lastUpdate);
      const now = new Date();
      
      // Adjust for timezone difference: subtract 10 hours
      const adjustedNow = new Date(now.getTime()); // optimize
      
      const diffMs = adjustedNow - lastUpdateTime;
      const diffHours = diffMs / (1000 * 60 * 60);
      
      let color;
      if (diffHours <= 24) {
        color = '#4CAF50';
      } else if (diffHours <= 24 * 3) {
        color = '#FF9800';
      } else {
        color = '#F44336';
      }
      
      // Format the time difference
      let text;
      if (diffHours < 1) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        text = `${diffMinutes}m ago`;
      } else if (diffHours < 24) {
        text = `${Math.floor(diffHours)}h ago`;
      } else {
        const diffDays = Math.floor(diffHours / 24);
        text = `${diffDays}d ago`;
      }
      
      return { text, color };
    } catch (error) {
      console.error('Error calculating time difference:', error);
      return { text: 'Invalid', color: '#ffffff' };
    }
  };




  const FilterMenu = ({ column }) => {
    const uniqueValues = [...new Set(devices.map(device => device[column]))];
    
    const handleSelectAllClick = (e) => {
      e.stopPropagation();
      if (filters[column].length === uniqueValues.length) {
        setFilters(prev => ({ ...prev, [column]: [] }));
      } else {
        setFilters(prev => ({ ...prev, [column]: uniqueValues }));
      }
    };  
    
    return (
      <Menu
        anchorEl={anchorEl}
        open={filterColumn === column}
        onClose={handleFilterClose}
        PaperProps={{
          sx: {
            backgroundColor: '#ffffff',
            color: '#000000'
          }
        }}
      >
        <MenuItem key="select-all" onClick={handleSelectAllClick}>
          <Checkbox 
            checked={filters[column].length === uniqueValues.length}
            indeterminate={filters[column].length > 0 && filters[column].length < uniqueValues.length}
            sx={{ color: '#4CAF50', '&.Mui-checked': { color: '#4CAF50' } }}
          />
          <ListItemText primary="Select All" />
        </MenuItem>
        <Divider key="divider" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}/>
          {uniqueValues.map(value => (
            <MenuItem key={`${column}-${value}`} onClick={() => handleFilterChange(column, value)}>
              <Checkbox 
                checked={filters[column].includes(value)}
                sx={{ color: '#4CAF50', '&.Mui-checked': { color: '#4CAF50' } }}
              />
              <ListItemText primary={value} />
            </MenuItem>
          ))}
      </Menu>
    );
  };


  const renderColumnHeader = (label, column, canFilter, canSort) => (
    <TableCell>
      <Stack direction="row" spacing={1} alignItems="center">
        {label}
        {canFilter && (
          <IconButton size="small" onClick={(e) => handleFilterClick(e, column)}>
            <FilterListIcon sx={{ 
              fontSize: 20, 
              color: filters[column] && filters[column].length > 0 ? '#4CAF50' : '#000000' 
            }} />
          </IconButton>
        )}
        {canSort && (
          <IconButton size="small" onClick={() => handleSort(column)}>
            <SortIcon 
              sx={{ 
                fontSize: 20, 
                color: sorting.column === column ? '#4CAF50' : '#000000',
                transform: sorting.column === column && sorting.direction === 'desc' ? 'rotate(180deg)' : 'none'
              }} 
            />
          </IconButton>
        )}
        {canFilter && filters[column] && <FilterMenu column={column} />}
      </Stack>
    </TableCell>
  );

  const handleEdit = (deviceId) => {
    navigate(`/admin/device/${deviceId}`);
  };

  const convertToLocalTime = (utcTimestamp) => {
    if (!utcTimestamp) return 'N/A';
    const date = new Date(utcTimestamp);

    const pad = (num) => num.toString().padStart(2, '0');

    const year = date.getUTCFullYear();
    const month = pad(date.getUTCMonth() + 1);
    const day = pad(date.getUTCDate());
    const hours = pad(date.getUTCHours());
    const minutes = pad(date.getUTCMinutes());
    const seconds = pad(date.getUTCSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

   const handleAddNote = (deviceId) => {
    setSelectedDeviceId(deviceId);
    setServiceNote({
      severity: 'green',
      serviceType: 'inspection',
      performedBy: '',
      description: '',
      serviceDescription: '',
      internalNotes: '',
      images: []
    });
    setOpenAddNoteDialog(true);
  }

 const handleImageUpload = async (event) => {
  const files = Array.from(event.target.files);
  
  const newImages = await Promise.all(files.map(async (file) => {

    const options = {
      maxSizeMB: 0.02,
      maxWidthOrHeight: 800, 
      useWebWorker: true,
      fileType: 'image/jpeg',
      initialQuality: 0.6,
      alwaysKeepResolution: false 
    };
    
    const compressedFile = await imageCompression(file, options);
    
    // Convert compressed file to base64
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(compressedFile);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
    
    return {
      file: compressedFile,
      preview: URL.createObjectURL(compressedFile),
      name: file.name,
      size: compressedFile.size,
      base64: base64,
      originalSize: file.size,
      compressedSize: compressedFile.size
    };
  }));
  
  setServiceNote(prev => ({
    ...prev,
    images: [...prev.images, ...newImages]
  }));
};

  const handleRemoveImage = (index) => {
    setServiceNote(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitServiceNote = async () => {
    try {
      const payload = {
        deviceId: selectedDeviceId,
        severity: serviceNote.severity || 'green',
        serviceType: serviceNote.serviceType || 'inspection',
        performedBy: serviceNote.performedBy || 'N/A',
        description: serviceNote.description || '',
        details: serviceNote.serviceDetails || '',
        notes: serviceNote.internalNotes || '',
        images: serviceNote.images.map(image => ({
          name: image.name,
          size: image.size,
          data: image.base64
        }))
      };

      const response = await fetch(`${API_BASE_URL}/admin/service-notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setOpenAddNoteDialog(false);
        alert('Service note added successfully!');
      } else {
        const errorData = await response.json();
        // todo
        alert(`Error adding service note: ${errorData.message}`);
      }

    } catch (error) {
      console.error('Error submitting service note:', error);
    }
  };

  return (
    <div className="admin-dashboard">
      <TableContainer component={Paper} sx={{ backgroundColor: '#cacaca', marginTop: '10px' }}>
        <Table>
          <TableHead>
            <TableRow>
              {Object.keys(searchTerms).map(column => (
                <TableCell key={`search-${column}`}>
                  <input
                    type="text"
                    value={searchTerms[column]}
                    onChange={(e) => handleSearch(column, e.target.value)}
                    placeholder={`Search ${column}...`}
                    className="search-input"
                  />
                </TableCell>
              ))}
              <TableCell align="right">
                <IconButton
                  onClick={handleClearAll}
                  sx={{
                    color: '#ffffff',
                    '&:hover': {
                      color: '#4CAF50',
                      backgroundColor: 'rgba(76, 175, 80, 0.08)'
                    }
                  }}
                >
                  {Object.values(searchTerms).some(term => term !== '') ? <FilterAltOffIcon /> : <FilterAltIcon />}
                </IconButton>
              </TableCell>
            </TableRow>
            <TableRow>
              {renderColumnHeader('Customer', 'customer', true, false)}
              {renderColumnHeader('Name', 'name', false, false)}
              {renderColumnHeader('Type', 'type', true, false)}
              {renderColumnHeader('Location', 'location', true, false)}
              {renderColumnHeader('Direction', 'direction', true, false)}
              {renderColumnHeader('Live', 'connected', true, false)}
              {renderColumnHeader('Mode', 'mode', true, false)}
              {renderColumnHeader('Signal', 'signal', false, true)}
              {renderColumnHeader('Battery', 'battery', false, true)}
              {renderColumnHeader('Battery °C', 'batterytemp', false, true)}
              {renderColumnHeader('Last Update', 'lastUpdate', false, true)}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredAndSortedDevices().map((device, index) => {
              const timeSinceUpdate = getTimeSinceLastUpdate(device.lastUpdate);
              
              return (
              <React.Fragment key={device.id}>
                <TableRow 
                    sx={{ backgroundColor: index % 2 ? '#e6e6e6' : '#f5f5f5' }}
                >
                    <TableCell>{device.customer}</TableCell>
                    <TableCell>{device.name}</TableCell>
                    <TableCell>{device.type}</TableCell>
                    <TableCell>{device.location}</TableCell>
                    <TableCell>{device.direction}</TableCell>
                    <TableCell>
                        {device.connected ? 
                            <CheckCircleIcon sx={{ color: 'green' }} /> : 
                            <CancelIcon sx={{ color: 'red' }} />
                        }
                    </TableCell>
                    <TableCell>{device.mode}</TableCell>
                    <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                              {getSignalIcon(device.signal)}
                              {device.signal} dBm
                          </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                          {getBatteryIcon(device.battery)}
                          {device.battery === -1 ? 'Fail' : `${device.battery}%`}
                      </Stack>
                    </TableCell>
                    <TableCell>{device.batterytemp}°C</TableCell>
                    <TableCell align="center" sx={{ color: timeSinceUpdate.color, padding: '8px' }}>
                      {device.lastUpdate
                        ? (() => {
                            try {
                              const utcDate = new Date(device.lastUpdate);
                              // Convert to Brisbane time (UTC+10)
                              const brisbaneTime = new Date(utcDate.getTime() + (10 * 60 * 60 * 1000)); // optimize
                              const utcTime = new Date(utcDate.getTime()); // optimize
                              const formattedDate = utcTime.toLocaleDateString('en-AU', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                              });
                              const formattedTime = utcTime.toLocaleTimeString('en-AU', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: true
                              });

                              return (
                                <>
                                  <Typography variant="body2" component="div">
                                    {formattedDate}
                                  </Typography>
                                  <Typography variant="body2" component="div">
                                    {formattedTime}
                                  </Typography>
                                  <Typography variant="caption" component="div" sx={{ color: timeSinceUpdate.color }}>
                                    ({timeSinceUpdate.text})
                                  </Typography>
                                </>
                              );
                            } catch (error) {
                              console.error('Error parsing date:', error);
                              return 'Invalid Date';
                            }
                          })()
                        : 'Unknown'}
                    </TableCell>
                    <TableCell align="right">
                    <IconButton onClick={() => handleEdit(device.id)}>
                      <OpenInNewIcon/>
                    </IconButton>
                    <IconButton onClick={() => handleAddNote(device.id)}>
                      <NoteAddIcon/>
                    </IconButton>
                    </TableCell>
                </TableRow>
              </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    <Dialog
      open={openAddNoteDialog}
      onClose={() => setOpenAddNoteDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{color: 'black'}}>Add Service Note - {devices.find(device => device.id === selectedDeviceId)?.name} - {devices.find(device => device.id === selectedDeviceId)?.location}</DialogTitle>
      <DialogContent>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Screen Status
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant={serviceNote.severity === 'green' ? "contained" : "outlined"}
              onClick={() => setServiceNote({...serviceNote, severity: 'green'})}
              sx={{
                backgroundColor: serviceNote.severity === 'green' ? '#4CAF50' : 'transparent',
                color: serviceNote.severity === 'green' ? 'white' : '#4CAF50',
                borderColor: '#4CAF50',
                '&:hover': {
                  backgroundColor: serviceNote.severity === 'green' ? '#45a049' : 'rgba(76, 175, 80, 0.1)',
                  borderColor: '#45a049'
                }
              }}
            >
              Good
            </Button>
            <Button
              variant={serviceNote.severity === 'yellow' ? "contained" : "outlined"}
              onClick={() => setServiceNote({...serviceNote, severity: 'yellow'})}
              sx={{
                backgroundColor: serviceNote.severity === 'yellow' ? '#FFC107' : 'transparent',
                color: serviceNote.severity === 'yellow' ? 'black' : '#FFC107',
                borderColor: '#FFC107',
                '&:hover': {
                  backgroundColor: serviceNote.severity === 'yellow' ? '#e0a800' : 'rgba(255, 193, 7, 0.1)',
                  borderColor: '#e0a800'
                }
              }}
            >
              Warning
            </Button>
            <Button
              variant={serviceNote.severity === 'red' ? "contained" : "outlined"}
              onClick={() => setServiceNote({...serviceNote, severity: 'red'})}
              sx={{
                backgroundColor: serviceNote.severity === 'red' ? '#F44336' : 'transparent',
                color: serviceNote.severity === 'red' ? 'white' : '#F44336',
                borderColor: '#F44336',
                '&:hover': {
                  backgroundColor: serviceNote.severity === 'red' ? '#d32f2f' : 'rgba(244, 67, 54, 0.1)',
                  borderColor: '#d32f2f'
                }
              }}
            >
              Fatal
            </Button>
            <Button
              variant={serviceNote.severity === 'grey' ? "contained" : "outlined"}
              onClick={() => setServiceNote({...serviceNote, severity: 'grey'})}
              sx={{
                backgroundColor: serviceNote.severity === 'grey' ? '#9e9e9e' : 'transparent',
                color: serviceNote.severity === 'grey' ? 'white' : '#9e9e9e',
                borderColor: '#9e9e9e',
                '&:hover': {
                  backgroundColor: serviceNote.severity === 'grey' ? '#757575' : 'rgba(158, 158, 158, 0.1)',
                  borderColor: '#757575'
                }
              }}
            >
              Retired
            </Button>
          </Stack>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          
          {/* Service Type Selector */}
          <FormControl fullWidth>
            <InputLabel>Type of Service</InputLabel>
            <Select
              value={serviceNote.serviceType}
              label="Type of Service"
              onChange={(e) => setServiceNote({...serviceNote, serviceType: e.target.value})}
            >
              <MenuItem value="inspection">Inspection</MenuItem>
              <MenuItem value="maintenance">Routine Maintenance</MenuItem>
              <MenuItem value="upgrade">Software/Hardware Upgrade</MenuItem>
              <MenuItem value="repair">Repair</MenuItem>
              <MenuItem value="calibration">Calibration</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          
          {/* Performed By */}
          <TextField
            label="Performed By"
            value={serviceNote.performedBy}
            onChange={(e) => setServiceNote({...serviceNote, performedBy: e.target.value})}
            fullWidth
            placeholder="Enter technician name"
          />
          
          {/* Description */}
          <TextField
            label="Description"
            value={serviceNote.description}
            onChange={(e) => setServiceNote({...serviceNote, description: e.target.value})}
            fullWidth
            multiline
            minRows={3}
            expandRows={true}
            placeholder="Brief description of the issue"
          />
          
          {/* Service Description */}
          <TextField
            label="Service Details"
            value={serviceNote.serviceDetails}
            onChange={(e) => setServiceNote({...serviceNote, serviceDetails: e.target.value})}
            fullWidth
            multiline
            minRows={4}
            placeholder="Detailed description of service performed"
          />
          <TextField
            label="Internal Notes"
            value={serviceNote.internalNotes}
            onChange={(e) => setServiceNote({...serviceNote, internalNotes: e.target.value})}
            fullWidth
            multiline
            minRows={4}
            placeholder="Internal technician notes"
          />
          
          {/* Image Upload Section */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Upload Images
            </Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="service-note-image-upload"
              type="file"
              multiple
              onChange={handleImageUpload}
              capture="environment"
            />
            <label htmlFor="service-note-image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={serviceNote.images.length >= 4}
                fullWidth
              >
                Upload Images
              </Button>
            </label>
            
            {/* Image Preview */}
            {serviceNote.images.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {serviceNote.images.length} image(s) selected
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {serviceNote.images.map((image, index) => (
                    <Box key={index} sx={{ position: 'relative', mb: 1 }}>
                      <img
                        src={image.preview}
                        alt={image.name}
                        style={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 4
                        }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          backgroundColor: 'white',
                          '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                        onClick={() => handleRemoveImage(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenAddNoteDialog(false)}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmitServiceNote}
          disabled={!serviceNote.serviceType || !serviceNote.performedBy}
        >
          Upload Service Note
        </Button>
      </DialogActions>
    </Dialog>
    </div>
  );
};

export default AdminDashboard;